/**
 * ProductCardWidget — voice mode wide PDP layout (2026-05-19 redesign).
 *
 * Single-product detail card emitted by `get_product_details` (1 SKU) and
 * `show_featured_product`. Two responsive layouts driven by
 * `useIsCompactVoiceLayout()`:
 *
 *  - **Desktop** (> 760px): 2-column grid. Left = image gallery (main image
 *    well + thumbnail strip). Right = brand / title / category / rating /
 *    Key Features / price + View Product CTA.
 *
 *  - **Mobile** (≤ 760px): single column. Image gallery on top, details
 *    below, footer (price + button) pinned to bottom of card with a hairline.
 *
 * Theming: all colors flow through `var(--voice-widget-*)` tokens populated
 * by `VoiceThemeProvider`. Defaults are conservative navy/gray so the
 * widget reads well outside a theme (e.g. dev preview).
 *
 * Image gallery: thumbnails show up to 5 SKU images (`imageUrls`). When
 * fewer than 5 are provided, empty placeholder thumbs fill the row so the
 * grid stays visually balanced. Clicking a thumb swaps the main image; the
 * active thumb gets a brand-colored border.
 */
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import type { ProductCardWidgetData } from '../../types/widgets';
import { useIsCompactVoiceLayout } from '../../hooks/useIsCompactVoiceLayout';
import { AmazonStarRating } from './AmazonStarRating';

const WIDGET_KEYFRAMES = `
@keyframes voice-widget-fade-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
`;

const THUMB_COUNT = 5;
const FG = 'var(--voice-widget-fg, #2B4168)';
const MUTED = 'var(--voice-widget-muted, #6B7280)';
const BORDER = 'var(--voice-widget-border, #E5E7EB)';
const SURFACE = 'var(--voice-widget-card-bg, #FFFFFF)';
const BUTTON_BG = 'var(--voice-widget-button-bg, #2B4168)';
const BUTTON_FG = 'var(--voice-widget-button-fg, #FFFFFF)';
const HAIRLINE = 'var(--voice-widget-hairline, #F1F1F4)';

const KEY_FEATURES_COLLAPSED_COUNT = 3;
const KEY_FEATURES_EXPAND_MS = 260;

export interface ProductCardWidgetProps {
  data: ProductCardWidgetData;
  /** Live voice overlay only — fold bullets after the first three. */
  collapseKeyFeatures?: boolean;
}

function ImagePlaceholder({ size }: { size: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={FG}
      strokeWidth={1.5}
      style={{ opacity: 0.3, display: 'block' }}
      aria-hidden
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-5-5L5 21" />
    </svg>
  );
}

function ImageWell({ src, alt, height, padding }: { src?: string; alt: string; height: number; padding: number }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [src]);

  const showImg = !!src && !broken;
  return (
    <div
      style={{
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        padding,
        height,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {showImg ? (
        <img
          src={src}
          alt={alt}
          onError={() => setBroken(true)}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : (
        <ImagePlaceholder size={48} />
      )}
    </div>
  );
}

function Thumb({
  src,
  active,
  onClick,
  index,
}: {
  src?: string;
  active: boolean;
  onClick?: () => void;
  index: number;
}) {
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    setBroken(false);
  }, [src]);

  const interactive = !!src && !!onClick;
  const showImg = !!src && !broken;

  return (
    <button
      type="button"
      onClick={interactive ? onClick : undefined}
      aria-label={`View ${index + 1}`}
      style={{
        flex: 1,
        aspectRatio: '1 / 1',
        background: SURFACE,
        border: `1.5px solid ${active ? FG : BORDER}`,
        borderRadius: 8,
        padding: 6,
        cursor: interactive ? 'pointer' : 'default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        transition: 'border-color 160ms ease-out',
        font: 'inherit',
        color: 'inherit',
      }}
    >
      {showImg ? (
        <img
          src={src}
          alt=""
          onError={() => setBroken(true)}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
        />
      ) : (
        <ImagePlaceholder size={20} />
      )}
    </button>
  );
}

function useImageGallery(data: ProductCardWidgetData): {
  mainSrc: string | undefined;
  thumbs: (string | undefined)[];
  activeIndex: number;
  setActiveIndex: (i: number) => void;
} {
  const ordered = useMemo(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    const push = (u?: string) => {
      if (!u || typeof u !== 'string') return;
      if (seen.has(u)) return;
      seen.add(u);
      out.push(u);
    };
    push(data.imageUrl);
    for (const u of data.imageUrls ?? []) push(u);
    return out;
  }, [data.imageUrl, data.imageUrls]);

  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    setActiveIndex(0);
  }, [ordered]);

  const thumbs: (string | undefined)[] = Array.from({ length: THUMB_COUNT }, (_, i) => ordered[i]);
  const mainSrc = ordered[Math.min(activeIndex, Math.max(0, ordered.length - 1))] ?? ordered[0];

  return { mainSrc, thumbs, activeIndex, setActiveIndex };
}

function CategoryBreadcrumb({
  category,
  subcategory,
  compact,
}: {
  category?: string;
  subcategory?: string;
  compact: boolean;
}) {
  const parts = compact ? [subcategory || category] : [category, subcategory];
  const label = parts.filter((p) => typeof p === 'string' && p.trim().length > 0).join(' · ');
  if (!label) return null;
  return (
    <div style={{ fontSize: 13, color: FG, opacity: 0.75, marginBottom: 14 }}>{label}</div>
  );
}

function FeatureBullet({ text }: { text: string }) {
  return (
    <li
      style={{
        fontSize: 13,
        lineHeight: 1.5,
        color: FG,
        padding: '6px 0 6px 22px',
        position: 'relative',
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 0,
          top: 6,
          color: FG,
          fontWeight: 700,
          fontSize: 13,
        }}
      >
        ✓
      </span>
      {text}
    </li>
  );
}

function KeyFeatures({
  bullets,
  collapseKeyFeatures,
}: {
  bullets: string[];
  collapseKeyFeatures?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const extraRef = React.useRef<HTMLUListElement>(null);
  const [extraMaxHeight, setExtraMaxHeight] = useState(0);
  // Stable signature — parent passes a fresh `bullets` array every render
  // (voice overlay re-renders on transcript ticks); must not reset expanded
  // when only the array reference changes.
  const bulletsSignature = bullets.join('\u0001');

  const canCollapse =
    collapseKeyFeatures && bullets.length > KEY_FEATURES_COLLAPSED_COUNT;
  const extraBullets = canCollapse ? bullets.slice(KEY_FEATURES_COLLAPSED_COUNT) : [];

  useLayoutEffect(() => {
    if (!canCollapse) {
      setExtraMaxHeight(0);
      return;
    }
    const el = extraRef.current;
    if (!el) return;
    setExtraMaxHeight(expanded ? el.scrollHeight : 0);
  }, [canCollapse, expanded, bulletsSignature]);

  if (bullets.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: FG,
          opacity: 0.7,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}
      >
        Key Features
      </div>
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>
        {(canCollapse ? bullets.slice(0, KEY_FEATURES_COLLAPSED_COUNT) : bullets).map((b, i) => (
          <FeatureBullet key={i} text={b} />
        ))}
      </ul>
      {canCollapse ? (
        <div
          style={{
            overflow: 'hidden',
            maxHeight: expanded ? extraMaxHeight : 0,
            opacity: expanded ? 1 : 0,
            transition: `max-height ${KEY_FEATURES_EXPAND_MS}ms ease-out, opacity ${KEY_FEATURES_EXPAND_MS}ms ease-out`,
          }}
          aria-hidden={!expanded}
        >
          <ul
            ref={extraRef}
            style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 0 }}
          >
            {extraBullets.map((b, i) => (
              <FeatureBullet key={i} text={b} />
            ))}
          </ul>
        </div>
      ) : null}
      {canCollapse ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          style={{
            alignSelf: 'flex-start',
            margin: 0,
            padding: 0,
            border: 'none',
            background: 'none',
            font: 'inherit',
            fontSize: 13,
            fontWeight: 500,
            color: FG,
            cursor: 'pointer',
            textDecoration: 'underline',
            textUnderlineOffset: 3,
          }}
        >
          {expanded ? 'See less' : 'See more'}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Combined View Product + price pill (2026-05-19). Replaces the previous
 * "Price label + price + standalone button" footer with a single dark
 * rounded button reading `View product  ·  $XX.XX`. Max width 400px so it
 * doesn't span an entire wide desktop card; centered when the parent
 * gives it room.
 */
function ViewProductPill({ price, href }: { price: string; href?: string }) {
  const sharedStyle: React.CSSProperties = {
    background: BUTTON_BG,
    color: BUTTON_FG,
    border: 'none',
    padding: '14px 28px',
    fontSize: 14,
    fontWeight: 500,
    borderRadius: 999,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    cursor: href ? 'pointer' : 'default',
    width: '100%',
    maxWidth: 400,
    boxSizing: 'border-box',
  };
  const inner = (
    <>
      <span>View product</span>
      {price ? (
        <>
          <span
            aria-hidden
            style={{
              width: 4,
              height: 4,
              borderRadius: '50%',
              background: BUTTON_FG,
              opacity: 0.85,
              display: 'inline-block',
              flexShrink: 0,
            }}
          />
          <span style={{ fontWeight: 600 }}>{price}</span>
        </>
      ) : null}
    </>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" style={sharedStyle}>
        {inner}
      </a>
    );
  }
  return <div style={sharedStyle}>{inner}</div>;
}

export const ProductCardWidget: React.FC<ProductCardWidgetProps> = ({
  data,
  collapseKeyFeatures = false,
}) => {
  const isCompact = useIsCompactVoiceLayout();
  const href = data.retailerProductPageUrl;
  const { mainSrc, thumbs, activeIndex, setActiveIndex } = useImageGallery(data);

  const bullets = useMemo(
    () =>
      (data.featureBullets ?? [])
        .map((b) => (typeof b === 'string' ? b.trim() : ''))
        .filter((b) => b.length > 0)
        .slice(0, 8),
    [data.featureBullets],
  );

  const Brand = data.brand ? (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: FG,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: 8,
      }}
    >
      {data.brand}
    </div>
  ) : null;

  const Title = (
    <h2
      style={{
        fontSize: isCompact ? 18 : 20,
        fontWeight: 600,
        lineHeight: 1.3,
        color: FG,
        margin: '0 0 8px',
      }}
    >
      {data.title}
    </h2>
  );

  const Rating = (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13,
        color: MUTED,
        marginBottom: 20,
        paddingBottom: 20,
        borderBottom: `1px solid ${HAIRLINE}`,
      }}
    >
      <AmazonStarRating
        amazonRating={data.amazonRating}
        amazonReviewCount={data.amazonReviewCount}
        size={14}
        countFontSize={13}
        format="long"
      />
    </div>
  );

  const Gallery = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <ImageWell src={mainSrc} alt={data.title} height={isCompact ? 240 : 320} padding={isCompact ? 20 : 24} />
      <div style={{ display: 'flex', gap: 8 }}>
        {thumbs.map((src, i) => (
          <Thumb
            key={i}
            src={src}
            index={i}
            active={i === activeIndex}
            onClick={src ? () => setActiveIndex(i) : undefined}
          />
        ))}
      </div>
    </div>
  );

  const Details = (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {Brand}
      {Title}
      <CategoryBreadcrumb category={data.category} subcategory={data.subcategory} compact={isCompact} />
      {Rating}
      <KeyFeatures
        key={data.stacklineSku}
        bullets={bullets}
        collapseKeyFeatures={collapseKeyFeatures}
      />
      <div
        style={{
          marginTop: isCompact ? 20 : 'auto',
          paddingTop: isCompact ? 16 : 24,
          borderTop: isCompact ? `1px solid ${HAIRLINE}` : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
        }}
      >
        <ViewProductPill price={data.price} href={href} />
      </div>
    </div>
  );

  return (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', boxSizing: 'border-box' }}>
      <style>{WIDGET_KEYFRAMES}</style>
      <article
        style={{
          width: '100%',
          maxWidth: isCompact ? 420 : 1024,
          background: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: 12,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: isCompact ? '1fr' : '1fr 1fr',
          minHeight: isCompact ? undefined : 520,
          boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
          animation: 'voice-widget-fade-in 0.28s ease-out both',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            padding: isCompact ? '20px 20px 0' : 32,
            background: SURFACE,
            borderRight: isCompact ? 'none' : `1px solid ${BORDER}`,
            borderBottom: isCompact ? `1px solid ${BORDER}` : 'none',
          }}
        >
          {Gallery}
        </div>
        <div style={{ padding: isCompact ? 20 : 32, display: 'flex' }}>{Details}</div>
      </article>
    </div>
  );
};

export default ProductCardWidget;
